import type { CellContext } from "@tanstack/react-table";
import type { VariantProps } from "../../lib/cva";
import type * as React from "react";

import {
  Badge,
  type BadgeProps,
  badgeVariants,
} from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Icon, type IconSource } from "../../components/ui/icon";
import { Text } from "../../components/ui/text";
import {
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
} from "../../components/ui/tooltip";
import {
  DataTableCellContent,
  DataTableHoverActionsCell,
} from "./data-table-cell";
import type {
  DataTableColumnDef,
  DataTableColumnMeta,
} from "./data-table-types";
import { omitUndefined, type Omittable } from "../../lib/props";

// ---------------------------------------------------------------------------
// formatShortDate
// ---------------------------------------------------------------------------

/**
 * Format a date string as "Jan 5, 2025". Returns "—" for nullish values.
 */
export function formatShortDate(dateStr: string | null | undefined): string {
  if (dateStr == null) return "—";
  return new Date(dateStr).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/**
 * "Jan 5, 2025, 2:30 PM" — the unabridged timestamp a `date` column's short
 * label expands to on hover. Returns "—" for nullish values.
 */
export function formatFullDateTime(dateStr: string | null | undefined): string {
  if (dateStr == null) return "—";
  const d = new Date(dateStr);
  const date = d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const time = d.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
  return `${date}, ${time}`;
}

// ---------------------------------------------------------------------------
// Badge map entry type
// ---------------------------------------------------------------------------

export type BadgeMapEntry = Omittable<{
  label: string;
  color?: BadgeProps["color"];
  tone?: VariantProps<typeof badgeVariants>["tone"];
  size?: VariantProps<typeof badgeVariants>["size"];
  /**
   * The resting state of a status column: the cell renders the label as
   * muted text instead of a chip, so only the exceptional values wear color.
   * A column where every row is "Active" says nothing N times; the quiet
   * entry keeps the value truthful and sortable without the repetition.
   * Applies to the cell only — a consumer rendering the same map as a group
   * header or a title chip still gets `color`/`tone`, since one chip naming
   * a band is not repetition.
   */
  quiet?: boolean;
}>;

// ---------------------------------------------------------------------------
// Column config types
// ---------------------------------------------------------------------------

type AccessorColumnConfig<TData> = Omittable<{
  accessorKey: keyof TData & string;
  /** Column id when it must differ from `accessorKey` — a table whose sort
   *  vocabulary is the server's orderBy keys names the column by that key
   *  while the cell still reads a differently-named row field. Defaults to
   *  `accessorKey`. */
  id?: string;
  header: string | React.ReactNode;
  enableSorting?: boolean;
  meta?: DataTableColumnMeta;
  cell?: (context: CellContext<TData, unknown>) => React.ReactNode;
}>;

type DisplayColumnConfig<TData> = Omittable<{
  id: string;
  header?: string | React.ReactNode;
  enableSorting?: boolean;
  meta?: DataTableColumnMeta;
  cell: (context: CellContext<TData, unknown>) => React.ReactNode;
}>;

type DateColumnConfig<TData> = Omittable<{
  accessorKey: keyof TData & string;
  header: string;
  enableSorting?: boolean;
  /** Merged over the helper's defaults (minWidth 120, subtle text). */
  meta?: DataTableColumnMeta;
}>;

type BadgeColumnConfig<TData> = {
  header: string;
  enableSorting?: boolean;
  badgeMap: Record<string, BadgeMapEntry>;
  fallback?: string;
  meta?: DataTableColumnMeta;
} & (
  | { accessorKey: keyof TData & string; id?: never; accessorFn?: never }
  | { id: string; accessorFn: (row: TData) => string; accessorKey?: never }
);

type HoverActionColumnConfig<TData> = Omittable<{
  accessorKey: keyof TData & string;
  header: string | React.ReactNode;
  enableSorting?: boolean;
  meta?: DataTableColumnMeta;
  content: (row: TData) => React.ReactNode;
  onClickAction: (row: TData) => void;
  actionLabel?: string | ((row: TData) => string);
  actionIcon?: IconSource;
  /** Whether pressing the content fires `onClickAction`. A predicate decides
   *  per row, for a list where some rows open and others are only named. */
  contentClickable?: boolean | ((row: TData) => boolean);
  showActionButton?: boolean;
  /** Extra hover-revealed actions rendered ahead of the built-in action
   *  button and the row's kebab (each its own squared `outline` icon
   *  Button). A press on one is the button's, never the cell's: the cell
   *  opens only on its own press (isOwnPress). */
  actions?: (row: TData) => React.ReactNode;
}>;

// ---------------------------------------------------------------------------
// createColumnHelper
// ---------------------------------------------------------------------------

export function createColumnHelper<TData>() {
  return {
    /**
     * General-purpose column. If no `cell` provided, the hook's existing
     * `defaultColumn.cell` auto-wraps the accessor value in `DataTableCell`.
     */
    accessor(config: AccessorColumnConfig<TData>): DataTableColumnDef<TData> {
      const col: DataTableColumnDef<TData> = omitUndefined({
        id: config.id,
        accessorKey: config.accessorKey as string,
        header: config.header as string,
        enableSorting: config.enableSorting ?? false,
        meta: config.meta,
      });
      if (config.cell) {
        col.cell = config.cell;
      }
      return col;
    },

    /**
     * No accessor — consumer provides cell. For custom/complex cells.
     */
    display(config: DisplayColumnConfig<TData>): DataTableColumnDef<TData> {
      return omitUndefined({
        id: config.id,
        header: (config.header ?? "") as string,
        enableSorting: config.enableSorting ?? false,
        meta: config.meta,
        cell: config.cell,
      });
    },

    /**
     * Date column with built-in `formatShortDate`. Defaults to sortable
     * with subtle text brightness. The cell carries the full timestamp in a
     * hover tooltip — the short label drops the time of day, so the hover is
     * where it stays reachable.
     */
    date(config: DateColumnConfig<TData>): DataTableColumnDef<TData> {
      return {
        accessorKey: config.accessorKey as string,
        header: config.header,
        enableSorting: config.enableSorting ?? true,
        meta: {
          minWidth: 120,
          textBrightness: "subtle" as const,
          ...config.meta,
        },
        cell: ({ row }) => {
          const value = (row.original as Record<string, unknown>)[
            config.accessorKey
          ] as string | null | undefined;
          const label = (
            <Text size={null} variant="muted" truncate>
              {formatShortDate(value)}
            </Text>
          );
          return (
            <DataTableCellContent>
              {value == null ? (
                label
              ) : (
                <Tooltip>
                  <TooltipTrigger render={label} />
                  <TooltipPopup side="top">{formatFullDateTime(value)}</TooltipPopup>
                </Tooltip>
              )}
            </DataTableCellContent>
          );
        },
      };
    },

    /**
     * Maps accessor value to Badge rendering. Supports `accessorKey` or
     * `accessorFn + id` for derived values.
     */
    badge(config: BadgeColumnConfig<TData>): DataTableColumnDef<TData> {
      const cellFn = ({ row }: CellContext<TData, unknown>) => {
        let value: string;
        if (config.accessorKey) {
          value = String(
            (row.original as Record<string, unknown>)[config.accessorKey] ?? "",
          );
        } else {
          value = config.accessorFn!(row.original);
        }

        const entry =
          config.badgeMap[value] ??
          (config.fallback ? config.badgeMap[config.fallback] : undefined);

        if (!entry) return null;

        return (
          <DataTableCellContent>
            {entry.quiet ? (
              <Text size={null} variant="muted" truncate>
                {entry.label}
              </Text>
            ) : (
              <Badge color={entry.color} tone={entry.tone} size={entry.size}>
                {entry.label}
              </Badge>
            )}
          </DataTableCellContent>
        );
      };

      const shared = {
        header: config.header,
        enableSorting: config.enableSorting ?? false,
        meta: config.meta,
        cell: cellFn,
      };

      if (config.accessorKey) {
        return {
          ...shared,
          accessorKey: config.accessorKey as string,
        } as DataTableColumnDef<TData>;
      }

      return {
        ...shared,
        id: config.id!,
        accessorFn: config.accessorFn as (row: TData) => string,
      } as DataTableColumnDef<TData>;
    },

    /**
     * Name-style column: content + optional trailing action button.
     * Defaults to a gradient header hover treatment.
     */
    hoverAction(
      config: HoverActionColumnConfig<TData>,
    ): DataTableColumnDef<TData> {
      return {
        accessorKey: config.accessorKey as string,
        header: config.header as string,
        enableSorting: config.enableSorting ?? true,
        meta: {
          minWidth: 250,
          truncate: true,
          ...config.meta,
          headerHoverStyle: config.meta?.headerHoverStyle ?? "gradient",
        },
        cell: ({ row }) => {
          const data = row.original;
          const explicitLabel =
            typeof config.actionLabel === "function"
              ? config.actionLabel(data)
              : config.actionLabel;
          const rawValue = (data as Record<string, unknown>)[
            config.accessorKey
          ];
          const fallbackLabel =
            typeof rawValue === "string" && rawValue.trim().length > 0
              ? typeof config.header === "string" &&
                config.header.trim().length > 0
                ? `${config.header}: ${rawValue}`
                : rawValue
              : typeof config.header === "string" &&
                  config.header.trim().length > 0
                ? config.header
                : "Open details";
          const label = explicitLabel ?? fallbackLabel;
          const iconSource = config.actionIcon ?? "chevron-right";
          const content = config.content(data);
          const contentClickable =
            typeof config.contentClickable === "function"
              ? config.contentClickable(data)
              : (config.contentClickable ?? true);
          const showActionButton = config.showActionButton ?? false;

          return (
            <DataTableHoverActionsCell
              onClick={
                contentClickable ? () => config.onClickAction(data) : undefined
              }
              aria-label={contentClickable ? label : undefined}
              actions={
                config.actions || showActionButton ? (
                  <>
                    {config.actions?.(data)}
                    {showActionButton ? (
                      <Button
                        variant="outline"
                        size="icon-sm"
                        onClick={() => config.onClickAction(data)}
                        aria-label={label}
                      >
                        {typeof iconSource === "string" ? (
                          <Icon name={iconSource} color="muted" />
                        ) : (
                          <Icon as={iconSource} color="muted" />
                        )}
                      </Button>
                    ) : null}
                  </>
                ) : null
              }
            >
              {content}
            </DataTableHoverActionsCell>
          );
        },
      };
    },
  };
}
